// 发送到客户Kakao / 카카오로 보내기 (YZY 10-07, 사장님 결정 ①안 #45)
// 견적서 화면에서 버튼 한 번 → 작업사진·청구서(견적서 화면 그림)·청구 금액을 자동으로 묶어 서버에 준비해 둔다.
// 실제 전송은 여기서 하지 않는다 — 카카오 상담창 옆 CS 助手 창에서 직원이 미리보기를 보고 「확인」을 눌러야 나간다.
// 서버: /api/cs/send/prepare (cs-api/index.js) — 이 화주와 본인확인으로 연결된 상담창을 찾아 준다.
import axios from 'axios';
import Vue from 'vue';
import html2canvas from 'html2canvas';
import { uploadImportPhoto } from '@/utils/importPhotoS3';
import CustomerBillTable from './CustomerBillTable.vue';
import { buildCustomerBill } from './customerBill';
import { canvasToPdfBlob } from '@/utils/imagePdf';

// 고객에게 보낼 사진 종류 — 商品及包装·需确认·错误. 尺寸(사이즈사진)은 내부 판독용이라 뺀다.
// 이전 입고에서 물려받은 사진(이전입고)도 뺀다 — 이번 작업 사진이 아니다.
const SEND_PHOTO_KINDS = ['입고사진', '확인사진', '오류사진'];
const MAX_PHOTOS = 30;

async function collectWorkPhotos(items) {
  const urls = [];
  for (const it of items) {
    if (!it || !it._id) continue;
    const res = await axios.post('/api/importPhoto/list', { _id: String(it._id) });
    if (res.data.요청결과 !== '성공') {
      throw new Error((res.data.error && res.data.error.errorMessage) || 'importPhoto/list 실패');
    }
    for (const p of res.data.입고사진 || []) {
      if (p && p.url && SEND_PHOTO_KINDS.includes(p.종류) && !p.이전입고 && !urls.includes(p.url)) urls.push(p.url);
    }
  }
  return urls.slice(0, MAX_PHOTOS);
}

// 고객용 账单 표를 그림으로 — 견적서 화면을 찍지 않는다(원가·물류비 열이 같이 찍히므로).
// 고객에게 보여도 되는 값만 뽑은 별도 표(CustomerBillTable)를 화면 밖에 잠깐 그려 찍고 바로 치운다. 입고사진과 같은 저장소에 올린다.
async function captureInvoice(bill, reqId) {
  const holder = document.createElement('div');
  holder.style.cssText = 'position:fixed;left:-10000px;top:0;z-index:-1;';
  document.body.appendChild(holder);
  const vm = new Vue({ render: h => h(CustomerBillTable, { props: { bill } }) }).$mount();
  holder.appendChild(vm.$el);
  try {
    const canvas = await html2canvas(vm.$el, { backgroundColor: '#ffffff', scale: 2, useCORS: true });
    const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
    if (!blob) throw new Error('견적서 그림을 만들지 못했습니다');
    const file = new File([blob], 'invoice.png', { type: 'image/png' });
    const invoiceUrl = await uploadImportPhoto(file, reqId);
    // 같은 그림을 PDF 한 장으로도 올린다. PDF 만 실패해도 그림은 그대로 보낸다(조용히 삼키지 않고 로그).
    let invoicePdfUrl = null;
    try {
      const pdfFile = new File([canvasToPdfBlob(canvas)], 'invoice.pdf', { type: 'application/pdf' });
      invoicePdfUrl = await uploadImportPhoto(pdfFile, reqId);
    } catch (e) {
      console.error('[kakaoSend] 청구서 PDF 만들기·올리기 실패 — 그림만 보냅니다', e);
    }
    return { invoiceUrl, invoicePdfUrl };
  } finally {
    vm.$destroy();
    document.body.removeChild(holder);
  }
}

/**
 * @param {{ group, amountKRW:number, rows:object[], customsType:string, header:object }} o  rows = 견적서 모달 행(quotationGroupItemsProcessed)
 * @returns {Promise<{ jobId, target, chatCandidates, chatUrl, photoCount }>}
 */
export async function prepareKakaoSend({ group, amountKRW, rows, customsType, header }) {
  const items = group.items || [];
  if (!items.length) throw new Error('보낼 건이 없습니다');
  if (!rows || !rows.length) throw new Error('견적서 행을 찾지 못했습니다');
  const photos = await collectWorkPhotos(items);
  const { invoiceUrl, invoicePdfUrl } = await captureInvoice(buildCustomerBill(rows, customsType, header), String(items[0]._id));
  const res = await axios.post('/api/cs/send/prepare', {
    companyid: group.companyid,
    companyName: group.companyName || '',
    화주주문번호: group.화주주문번호 || '',
    reqIds: items.map(i => String(i._id)),
    photos,
    invoiceUrl,
    ...(invoicePdfUrl ? { invoicePdfUrl } : {}),
    amountKRW: Math.round(Number(amountKRW) || 0),
    screen: 'reqpurchase-quotation'
  });
  if (!res.data || res.data.ok !== true) throw new Error((res.data && res.data.error) || 'prepare 실패');
  return Object.assign({ photoCount: photos.length }, res.data);
}
